/**
 * Raw auth API calls for the fleet-api auth surface.
 *
 * IMPORTANT: These functions use the native fetch API directly, NOT apiFetch
 * from api-client.ts. This is intentional to avoid a circular dependency:
 * api-client.ts dynamically imports refreshRequest from this file to handle
 * 401 token rotation. Using apiFetch here would create a circular import cycle.
 *
 * All endpoints consumed: /v1/auth/login, /v1/auth/refresh,
 * /v1/auth/logout, /v1/auth/me.
 */
import { env } from '@/lib/env';
import { authStorage } from '@/lib/auth-storage';
import {
  LoginResponseSchema,
  RefreshResponseSchema,
  UserSchema,
  LoginError,
  type LoginResponse,
  type RefreshResponse,
  type User,
} from './schemas';

/**
 * Base URL for the fleet API, resolved at CALL time (2026-06-10).
 *
 * Was a module-load-time `const BASE_URL = env.FLEET_API_URL`. This module
 * sits in the pre-auth entry chunk and therefore evaluates BEFORE the
 * boot-time /config.json runtime override is fetched (see
 * src/lib/runtime-config.ts); a load-time capture would have permanently
 * pinned the build-time URL on internal deployments. Resolving per call
 * keeps the DigitalOcean behavior identical (no config.json -> same
 * build-time value) while letting an internal config.json repoint auth.
 *
 * @returns The current fleet API REST base URL.
 */
function BASE_URL(): string {
  return env.FLEET_API_URL;
}

/**
 * POST /v1/auth/login
 *
 * Submits email + password credentials. On success returns the full token pair
 * and user object. On 401 throws LoginError('invalid_credentials'). On 429
 * throws LoginError('rate_limited') with the retry_after_seconds value.
 *
 * @param email - User's email address
 * @param password - User's plaintext password (sent over TLS)
 * @returns Validated LoginResponse on success
 * @throws LoginError on credential or rate-limit failure
 * @throws Error on unexpected HTTP status or network failure
 */
export async function loginRequest(email: string, password: string): Promise<LoginResponse> {
  // fleet-api's POST /v1/auth/login expects `identifier` (short username
  // OR full email), not `email`. Phase 1 deliberately broadened the field
  // so internal staff can sign in as "sigsentry" instead of always typing
  // "sigsentry@signalsentry.local". The UX still labels the field "Email"
  // because every customer-facing user logs in with an email; the wire
  // contract just stays permissive.
  const res = await fetch(`${BASE_URL()}/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier: email, password }),
  });

  if (res.status === 401) {
    throw new LoginError('invalid_credentials');
  }

  if (res.status === 429) {
    let retryAfter: number | undefined;
    try {
      const body = (await res.json()) as { detail: string; retry_after_seconds?: number };
      retryAfter = body.retry_after_seconds;
    } catch {
      // Ignore parse errors
    }
    throw new LoginError('rate_limited', retryAfter);
  }

  if (!res.ok) {
    throw new Error(`login_failed_${res.status}`);
  }

  const body = await res.json();
  return LoginResponseSchema.parse(body);
}

/**
 * POST /v1/auth/refresh
 *
 * Submits a refresh token (rotation-on-use). On success returns a new token
 * pair. On 401 the refresh token is invalid or expired.
 *
 * @param refreshToken - The current refresh JWT
 * @returns New RefreshResponse with rotated tokens
 * @throws Error on any non-200 response
 */
export async function refreshRequest(refreshToken: string): Promise<RefreshResponse> {
  const res = await fetch(`${BASE_URL()}/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!res.ok) {
    throw new Error(`refresh_failed_${res.status}`);
  }

  const body = await res.json();
  return RefreshResponseSchema.parse(body);
}

/**
 * POST /v1/auth/logout
 *
 * Revokes the current refresh token server-side and clears the HttpOnly cookie.
 * Requires a valid Bearer access token in the Authorization header.
 *
 * Failure (network error, non-204) is intentionally swallowed: the caller
 * always clears local storage regardless. A 15-min access token window makes
 * the server-side revocation non-critical for the client.
 */
export async function logoutRequest(): Promise<void> {
  const token = authStorage.getAccess();
  const refreshToken = authStorage.getRefresh();

  try {
    await fetch(`${BASE_URL()}/v1/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: refreshToken ? JSON.stringify({ refresh_token: refreshToken }) : undefined,
    });
  } catch {
    // Network failure on logout is non-fatal. Tokens are cleared locally regardless.
  }
}

/**
 * GET /v1/auth/me
 *
 * Returns the authenticated user's profile. Claims-only on the server
 * (ADR-003: zero DB lookups on the hot auth path).
 *
 * @returns Validated User object
 * @throws Error on 401 (token invalid/expired) or network failure
 */
export async function meRequest(): Promise<User> {
  const token = authStorage.getAccess();

  const res = await fetch(`${BASE_URL()}/v1/auth/me`, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) {
    throw new Error(`me_failed_${res.status}`);
  }

  const body = await res.json();
  return UserSchema.parse(body);
}
